import { Cell, Grid, HeadingSection, InfoLabel, Text } from 'bold-ui'
import { Box } from 'components/Box'
import { DateTime } from 'components/date'
import { booleanToSimNaoTitleCase } from 'components/form'
import { justificativaNaoPossuiCpfRecord } from 'components/form/field/select/JustificativaNaoPossuiCpfSelectField'
import { Cns, Cpf } from 'components/label'
import { useFlags } from 'config/useFlagsContext'
import {
  CidadaoVisualizacaoQuery,
  NacionalidadeEnum,
  RacaCorDbEnum,
  TipoLocalNascimentoEnum,
} from 'graphql/types.generated'
import { Fragment } from 'react'
import { identidadeGeneroRecord, nacionalidade, orientacaoSexual, sexoRecord, tipoSanguineoEnum } from 'types/enums'

import { CidadaoDocumentosAssociados } from '../../create/components/CidadaoDocumentosAssociados'

interface DadosPessoaisSectionProps {
  cidadao: CidadaoVisualizacaoQuery['cidadao']
}

export const DadosPessoaisSection = (props: DadosPessoaisSectionProps) => {
  const { cidadao } = props
  const { GESTAO_DOCUMENTO_CIDADAO_ENABLED } = useFlags()
  const isIndigena = cidadao?.racaCor?.racaCorDbEnum === RacaCorDbEnum.INDIGENA

  const isCidadaoAldeado = !!cidadao?.cidadaoAldeado

  const isBrasileira = cidadao?.nacionalidade?.nacionalidadeDbEnum === NacionalidadeEnum.BRASILEIRA

  const outrosDocumentos = (cidadao?.documentosAssociados ?? []).filter(
    ({ documento }) => documento !== cidadao?.cpf && documento !== cidadao?.cns
  )

  return (
    <HeadingSection level={2} title='Dados pessoais'>
      <Grid wrap>
        <Cell size={4}>
          <InfoLabel title='CPF'>
            {!cidadao?.cpf && cidadao?.justificativaNaoPossuiCpf ? (
              <Text fontStyle='italic'>
                Não possui ({justificativaNaoPossuiCpfRecord[cidadao?.justificativaNaoPossuiCpf]})
              </Text>
            ) : (
              <Cpf value={cidadao?.cpf} />
            )}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Nº NIS (PIS/PASEP)'>{cidadao?.nisPisPasep}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='CNS'>
            <Cns value={cidadao?.cns} />
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='DNV'>{cidadao?.numeroDnv}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Data de nascimento'>
            <DateTime format='DD/MM/YYYY' value={cidadao?.dataNascimento} />
          </InfoLabel>
        </Cell>
        {isCidadaoAldeado && (
          <Cell size={4}>
            <InfoLabel title='Data de reconhecimento'>
              <DateTime format='DD/MM/YYYY' value={cidadao?.cidadaoAldeado?.dtReconhecimento} />
            </InfoLabel>
          </Cell>
        )}
        <Cell size={4}>
          <InfoLabel title='Sexo'>{sexoRecord[cidadao?.sexo]}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Estado civil'>{cidadao?.estadoCivil?.nome.titleCase()}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Raça/cor'>{cidadao?.racaCor?.nome?.titleCase()}</InfoLabel>
        </Cell>
        {isIndigena && (
          <Cell size={4}>
            <InfoLabel placeholder='-' title='Etnia'>
              {cidadao?.etnia?.nome?.titleCase()}
            </InfoLabel>
          </Cell>
        )}
        <Cell size={!isIndigena && !isCidadaoAldeado ? 8 : 4}>
          <InfoLabel title='Tipo sanguíneo'>{tipoSanguineoEnum[cidadao?.tipoSanguineoEnum]}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Nome da mãe'>{cidadao?.nomeMae?.titleCase()}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Nome do pai'>{cidadao?.nomePai?.titleCase()}</InfoLabel>
        </Cell>
        {isCidadaoAldeado && (
          <Fragment>
            <Cell size={4}>
              <InfoLabel title='Função Social'>{cidadao?.cidadaoAldeado?.funcaoSocial?.nome?.titleCase()}</InfoLabel>
            </Cell>
            <Cell size={4}>
              <InfoLabel title='Chefe da família'>
                {booleanToSimNaoTitleCase(cidadao?.cidadaoAldeado?.stChefeFamilia)}
              </InfoLabel>
            </Cell>
            <Cell size={4}>
              <InfoLabel title='Responsável Legal'>{cidadao?.cidadaoAldeado?.responsavelLegal?.titleCase()}</InfoLabel>
            </Cell>
          </Fragment>
        )}
        {(!isIndigena || !isCidadaoAldeado) && <Cell size={4} />}
        <Cell size={4}>
          <InfoLabel title='Orientação sexual'>{orientacaoSexual[cidadao?.orientacaoSexualDbEnum]}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Identidade de gênero'>{identidadeGeneroRecord[cidadao?.identidadeGeneroDbEnum]}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Nacionalidade'>{nacionalidade[cidadao?.nacionalidade?.nacionalidadeDbEnum]}</InfoLabel>
        </Cell>
        {isBrasileira && (
          <Cell size={4}>
            <InfoLabel title='Município de nascimento'>
              {[cidadao?.localidadeNascimento?.nome?.titleCase(), cidadao?.localidadeNascimento?.uf?.sigla]
                .filter(Boolean)
                .join(' - ')}
            </InfoLabel>
          </Cell>
        )}
        {isBrasileira && cidadao.tipoLocalNascimento === TipoLocalNascimentoEnum.ALDEIA && (
          <Fragment>
            <Cell size={4}>
              <InfoLabel title='Aldeia de nascimento'>
                {cidadao?.cidadaoAldeado?.aldeiaNascimento?.nome?.titleCase()}
              </InfoLabel>
            </Cell>
            <Cell size={4}>
              <InfoLabel title='Local de ocorrência de nascimento'>
                {cidadao?.cidadaoAldeado?.localOcorrencia?.nome?.titleCase()}
              </InfoLabel>
            </Cell>
          </Fragment>
        )}
        {cidadao?.nacionalidade?.nacionalidadeDbEnum === NacionalidadeEnum.NATURALIZADA && (
          <>
            <Cell size={4}>
              <InfoLabel title='Portaria de naturalização'>{cidadao?.portariaNaturalizacao?.titleCase()}</InfoLabel>
            </Cell>

            <Cell size={4}>
              <InfoLabel title='Data de naturalização'>
                <DateTime format='DD/MM/YYYY' value={cidadao?.dataNaturalizacao} />
              </InfoLabel>
            </Cell>
          </>
        )}
        {cidadao?.nacionalidade?.nacionalidadeDbEnum === NacionalidadeEnum.ESTRANGEIRA && (
          <>
            <Cell size={4}>
              <InfoLabel title='País de nascimento'>{cidadao?.paisNascimento?.nome?.titleCase()}</InfoLabel>
            </Cell>
            <Cell size={4}>
              <InfoLabel title='Data de entrada no Brasil'>
                <DateTime format='DD/MM/YYYY' value={cidadao?.dataEntradaBrasil} />
              </InfoLabel>
            </Cell>
          </>
        )}
        {GESTAO_DOCUMENTO_CIDADAO_ENABLED && !outrosDocumentos.isEmpty() && (
          <Cell size={12}>
            <Box>
              <CidadaoDocumentosAssociados documentosAssociados={outrosDocumentos} />
            </Box>
          </Cell>
        )}
      </Grid>
    </HeadingSection>
  )
}
